import { type Action, type Reducer } from "redux";
import { type DictionaryLoadable } from "~/app/store/loadableTypes";

interface LoadingAction extends Action<string> {
  key: string;
}

interface LoadingSuccessAction<T> extends Action<string> {
  key: string;
  data: T | undefined;
}

interface LoadingErrorAction extends Action<string> {
  key: string;
}

interface LoadingBulkAction extends Action<string> {
  keys: string[];
}

interface LoadingBulkFinishAction<T> extends Action<string> {
  data: { [key: string]: T };
  errorKeys: string[];
}

type Actions<T> =
  | LoadingAction
  | LoadingSuccessAction<T>
  | LoadingErrorAction
  | LoadingBulkAction
  | LoadingBulkFinishAction<T>;

type ReturnData<T> = {
  actions: {
    load: (key: string) => LoadingAction;
    success: (key: string, data: T | undefined) => LoadingSuccessAction<T>;
    error: (key: string) => LoadingErrorAction;
  };

  reducer: Reducer<DictionaryLoadable<T>, Actions<T>>;
};

export function createLoadableDictionaryReducer<T>(prefix: string): ReturnData<T> {
  const LOADING_TYPE = prefix + "_LOADING";
  const SUCCESS_TYPE = prefix + "_SUCCESS";
  const ERROR_TYPE = prefix + "_ERROR";
  const BULK_LOADING_TYPE = prefix + "_BULK_LOADING";
  const BULK_FINISH_TYPE = prefix + "_BULK_FINISH";

  return {
    actions: {
      load: (key) => ({ type: LOADING_TYPE, key }),
      success: (key, data) => ({ type: SUCCESS_TYPE, key, data }),
      error: (key) => ({ type: ERROR_TYPE, key }),
    },

    reducer: (state = {}, action) => {
      if (action.type === LOADING_TYPE) {
        const { key } = action as LoadingAction;
        return {
          ...state,
          [key]: { state: "loading" },
        };
      }
      if (action.type === SUCCESS_TYPE) {
        const { key, data } = action as LoadingSuccessAction<T>;
        return {
          ...state,
          [key]: { state: "loaded", data },
        };
      }
      if (action.type === ERROR_TYPE) {
        const { key } = action as LoadingErrorAction;
        return {
          ...state,
          [key]: { state: "error" },
        };
      }
      if (action.type === BULK_LOADING_TYPE) {
        const { keys } = action as LoadingBulkAction;
        const newState = { ...state };
        for (const key of keys) {
          newState[key] = { state: "loading" };
        }
        return newState;
      }
      if (action.type === BULK_FINISH_TYPE) {
        const { data, errorKeys } = action as LoadingBulkFinishAction<T>;
        const newState = { ...state };
        for (const [key, value] of Object.entries(data)) {
          newState[key] = { state: "loaded", data: value };
        }
        for (const key of errorKeys) {
          newState[key] = { state: "error" };
        }
        return newState;
      }
      return state;
    },
  };
}
